import { useState, type ReactNode } from 'react';
import { CalendarClock, ChevronDown, HardDrive, Cloud } from 'lucide-react';
import { cn } from '@/utils/cn';
import ToggleSwitch from '../../common/ToggleSwitch';
import { Input } from '../../design/Input';
import { Select } from '../../design/Select';
import { Field } from '../settings/primitives';

const HOURS = Array.from({ length: 24 }, (_, h) => `${String(h).padStart(2, '0')}:00`);

const Chip = ({ on, onClick, icon, children }: { on: boolean; onClick: () => void; icon: ReactNode; children: ReactNode }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={on}
    className={cn(
      'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs border transition-colors',
      on ? 'bg-ember/15 border-ember/40 text-ember' : 'border-line text-fg-muted hover:text-fg hover:border-line-strong',
    )}
  >
    {icon} {children}
  </button>
);

const Keep = ({ value, onChange }: { value: number; onChange: (n: number) => void }) => (
  <span className="inline-flex items-center gap-2 text-xs text-fg-muted">
    behalte die letzten
    <input
      type="number"
      min={1}
      max={100}
      value={value}
      onChange={(e) => onChange(Math.max(1, Math.min(100, parseInt(e.target.value, 10) || 1)))}
      className="w-14 h-7 rounded-md bg-black/25 border border-line px-2 text-center font-mono text-fg focus:outline-none focus:border-ember/60"
    />
  </span>
);

interface RowProps {
  title: string;
  hint: string;
  enabled: boolean;
  onToggle: (v: boolean) => void;
  hdd: boolean;
  cloud: boolean;
  onHdd: () => void;
  onCloud: () => void;
  children?: ReactNode;
}

const ScheduleRow = ({ title, hint, enabled, onToggle, hdd, cloud, onHdd, onCloud, children }: RowProps) => (
  <div className={cn('rounded-xl border p-4 transition-colors', enabled ? 'border-line-strong bg-white/[0.02]' : 'border-line')}>
    <div className="flex items-center justify-between gap-4">
      <div className="min-w-0">
        <div className="text-sm text-fg">{title}</div>
        <div className="text-xs text-fg-muted mt-0.5">{hint}</div>
      </div>
      <ToggleSwitch enabled={enabled} onChange={onToggle} />
    </div>
    {enabled && (
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-3 pt-3 border-t border-line">
        {children}
        <span className="inline-flex items-center gap-2 text-xs text-fg-muted">
          speichern auf
          <Chip on={hdd} onClick={onHdd} icon={<HardDrive size={12} />}>HDD</Chip>
          <Chip on={cloud} onClick={onCloud} icon={<Cloud size={12} />}>Cloud</Chip>
        </span>
      </div>
    )}
  </div>
);

interface Props {
  config: Record<string, any>;
  serverName: string;
  onChange: (key: string, value: any) => void;
}

/** Zeitplan in Alltagssprache: „Jeden Tag um 04:00, behalte die letzten 2, speichern auf HDD". */
export const BackupSchedule = ({ config, serverName, onChange }: Props) => {
  const [more, setMore] = useState(false);
  // HDD ist Standard (an, solange nicht ausdrücklich aus), Cloud muss man einschalten.
  const hdd = (k: string) => config[`${k}_hdd`] !== false;
  const cloud = (k: string) => config[`${k}_cloud`] === true;
  const flip = (k: string, target: 'hdd' | 'cloud') => () =>
    onChange(`${k}_${target}`, target === 'hdd' ? !hdd(k) : !cloud(k));
  const time = config.schedule_time || '04:00';
  const hourOptions = (HOURS.includes(time) ? HOURS : [time, ...HOURS]).map((h) => ({ value: h, label: h }));

  return (
    <div className="bg-surface border border-line rounded-2xl overflow-hidden">
      <div className="flex items-center gap-2 px-5 py-3.5 border-b border-line text-sm font-medium text-fg">
        <CalendarClock size={15} className="text-fg-muted" /> Zeitplan
        <span className="text-xs font-normal text-fg-muted">automatische Backups laufen nur, solange der Server läuft</span>
      </div>
      <div className="p-5 space-y-3">
        <ScheduleRow
          title="Jeden Tag"
          hint="Eine Sicherung pro Tag – für die meisten Server genau richtig."
          enabled={!!config.daily}
          onToggle={(v) => onChange('daily', v)}
          hdd={hdd('daily')} cloud={cloud('daily')} onHdd={flip('daily', 'hdd')} onCloud={flip('daily', 'cloud')}
        >
          <span className="inline-flex items-center gap-2 text-xs text-fg-muted">
            um
            <Select className="w-24" buttonClassName="h-7 py-0 text-xs font-mono" value={time} onChange={(v) => onChange('schedule_time', v)} options={hourOptions} />
            Uhr
          </span>
          <Keep value={config.retention_daily ?? 2} onChange={(n) => onChange('retention_daily', n)} />
        </ScheduleRow>

        <ScheduleRow
          title="Jede Stunde"
          hint="Für Bau- und Creative-Server, auf denen viel passiert."
          enabled={!!config.hourly}
          onToggle={(v) => onChange('hourly', v)}
          hdd={hdd('hourly')} cloud={cloud('hourly')} onHdd={flip('hourly', 'hdd')} onCloud={flip('hourly', 'cloud')}
        >
          <Keep value={config.retention_hourly ?? 12} onChange={(n) => onChange('retention_hourly', n)} />
        </ScheduleRow>

        <ScheduleRow
          title="Beim Stoppen des Servers"
          hint="Sichert jedes Mal, wenn der Server heruntergefahren wird."
          enabled={!!config.on_stop}
          onToggle={(v) => onChange('on_stop', v)}
          hdd={hdd('on_stop')} cloud={cloud('on_stop')} onHdd={flip('on_stop', 'hdd')} onCloud={flip('on_stop', 'cloud')}
        >
          <Keep value={config.retention_count ?? 7} onChange={(n) => onChange('retention_count', n)} />
        </ScheduleRow>

        <div className="pt-1">
          <button type="button" onClick={() => setMore((m) => !m)} className="text-xs font-medium text-fg-muted hover:text-fg inline-flex items-center gap-1.5">
            <ChevronDown size={14} className={cn('transition-transform', more && 'rotate-180')} />
            Erweitert: Ordner ausschließen, Kompression, Cloud-Ordner
          </button>
          {more && (
            <div className="grid sm:grid-cols-2 gap-5 mt-4">
              <div className="sm:col-span-2">
                <Field label="Diese Ordner nicht sichern" hint="Durch Komma getrennt – z.B. große Karten-Caches, die sich neu erzeugen lassen.">
                  <Input value={config.ignored_files || ''} placeholder="logs, dynmap, plugins/dynmap" onChange={(e) => onChange('ignored_files', e.target.value)} />
                </Field>
              </div>
              <Field label="Kompression (Cloud)" hint="Stärker = kleiner, aber langsamer.">
                <Select
                  value={String(config.compression_level ?? 6)}
                  onChange={(v) => onChange('compression_level', parseInt(v, 10))}
                  options={[
                    { value: '0', label: 'Keine (am schnellsten)' },
                    { value: '1', label: 'Schnell' },
                    { value: '6', label: 'Normal' },
                    { value: '9', label: 'Stark (am kleinsten)' },
                  ]}
                />
              </Field>
              <Field label="Ordner in Google Drive" hint={`Leer = „${serverName}"`}>
                <Input value={config.drive_folder || ''} placeholder={serverName} onChange={(e) => onChange('drive_folder', e.target.value)} />
              </Field>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
